/**
 * SectionManager
 * Manages the list of sections on a page with drag-and-drop reordering.
 */

import { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus, Layers } from 'lucide-react'
import { SortableSectionCard } from './SortableSectionCard'
import { AddSectionDialog } from './AddSectionDialog'
import { SectionEditorDialog } from './SectionEditorDialog'
import { Button } from '@/components/ui/button'
import { useReorderSections } from '@/hooks/use-page-sections'
import type { Page, PageSection } from '@limo/database'

interface SectionManagerProps {
  page: Page
  sections: PageSection[]
  accountId: string
}

export function SectionManager({
  page,
  sections,
  accountId,
}: SectionManagerProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editingSection, setEditingSection] = useState<PageSection | null>(null)

  const reorderSections = useReorderSections()

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id)
      const newIndex = sections.findIndex((s) => s.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(sections, oldIndex, newIndex)
        const sectionIds = newOrder.map((s) => s.id)

        reorderSections.mutate({
          pageId: page.id,
          sectionIds,
        })
      }
    }
  }

  // Get section IDs for sortable context
  const sectionIds = sections.map((s) => s.id)

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950">
        <div className="flex items-start gap-3">
          <Layers className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100">
              Page Sections
            </h3>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              Drag sections to reorder them. Click on a section to edit its
              content. Toggle visibility to show or hide sections without
              deleting them.
            </p>
          </div>
        </div>
      </div>

      {/* Add Section Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">
          {sections.length} {sections.length === 1 ? 'Section' : 'Sections'}
        </h2>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Section
        </Button>
      </div>

      {/* Empty State */}
      {sections.length === 0 && (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <Layers className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No sections yet</h3>
          <p className="mt-1 text-muted-foreground">
            Add sections to build your page content.
          </p>
          <Button onClick={() => setAddDialogOpen(true)} className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            Add First Section
          </Button>
        </div>
      )}

      {/* Sections List with Drag and Drop */}
      {sections.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sectionIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {sections.map((section, index) => (
                <SortableSectionCard
                  key={section.id}
                  section={section}
                  index={index}
                  pageId={page.id}
                  onEdit={() => setEditingSection(section)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Another Section Button (shown when there are sections) */}
      {sections.length > 0 && (
        <div className="flex justify-center pt-4">
          <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Another Section
          </Button>
        </div>
      )}

      {/* Add Section Dialog */}
      <AddSectionDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        pageId={page.id}
        accountId={accountId}
      />

      {/* Edit Section Dialog */}
      <SectionEditorDialog
        open={!!editingSection}
        onOpenChange={(open) => {
          if (!open) setEditingSection(null)
        }}
        section={editingSection}
        accountId={accountId}
        pageId={page.id}
      />
    </div>
  )
}
